/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { CardGridButtonProps, CardGridGroup, CardGridItemProps } from './CardGrid';
import { frameworks, Group, ratings, regulatory, standards } from '@g17eco/core';
import { BaseScopeQuestion, CardProps } from '../../types/surveyScope';
import { ViewMap, ViewValues } from '../survey-overview-sidebar/viewOptions';
import { filterStandardAndFramework } from '../survey/utils/filters';
import { ROUTES } from '../../constants/routes';
import { generateUrl } from '../../routes/util';
import { AccessType, MetricGroup } from '../../types/metricGroup';
import { getCardIcon } from './CustomMetricCards';
import { getScopeInfo } from './scopeSelection';
import { hasRequiredTags } from '../../constants/groups';
import { getCardGroups } from './cardGroups';
import { CardGroupName, getSortedCards, getSortedCustomMetricCards } from './utils';
import { SurveyCardsFilter } from './partials/SurveyCardsFilter';
import { QUESTION } from '../../constants/terminology';

export interface PackAccumulator {
  latest: CardGridItemProps[];
  archived: CardGridItemProps[];
  latestMap: { [key: string]: CardGridItemProps };
}
export const filterCombinedCards = (combinedCards: CardGridItemProps[]) => {
  return combinedCards.reduce(
    (acc, pack) => {
      if (!pack.versionGroupCode || !pack.version) {
        acc.latest.push(pack);
        return acc;
      }

      const duplicatedPack = acc.latestMap[pack.versionGroupCode];
      if (duplicatedPack) {
        if (pack.version > (duplicatedPack.version || 0)) {
          acc.latest = acc.latest.filter((existedPack) => existedPack.versionGroupCode !== duplicatedPack.versionGroupCode);
          acc.latest.push(pack);
          acc.archived.push(duplicatedPack);
        } else {
          acc.archived.push(pack);
        }
      } else {
        acc.latest.push(pack);
        acc.latestMap[pack.versionGroupCode] = pack;
      }

      return acc;
    },
    { latest: [], archived: [], latestMap: {} } as PackAccumulator
  );
};

export const getStandardsCards = (props: CardProps) => _getCards(standards, props);
export const getFrameworksCards = (props: CardProps) => _getCards(frameworks, props);
export const getRegulatoryCards = (props: CardProps) => _getCards(regulatory, props);
export const getRatingsCards = (props: CardProps) => _getCards(ratings, props);

export const getRequiredCustomMetrics = (props: CardProps) => {
  const { surveyData, breadcrumbs, cardGroup, metricGroups, addBtn, handleDrilldown, scopeType, materiality } = props;

  const cardGroups: CardGridGroup[] = [];
  const isDrilldown = breadcrumbs && breadcrumbs.length > 0;
  const initiativeId = surveyData?.initiativeId;
  const surveyId = surveyData?._id;

  const getButtons: (metricGroup: MetricGroup) => CardGridButtonProps[] = (metricGroup) => {
    const buttons: CardGridButtonProps[] = [];

    const inScope = surveyData.scope?.custom.includes(metricGroup._id);

    if (metricGroup.accessType !== AccessType.Assigned) {
      buttons.push({
        icon: <i className='fal fa-cog' />,
        tooltip: 'Edit Custom Metrics',
        to: `${generateUrl(ROUTES.CUSTOM_METRICS, { initiativeId, groupId: metricGroup._id })}?surveyId=${surveyId}`,
      });
    }

    if (metricGroup.groupData?.link) {
      buttons.push({
        icon: <i className='fa fa-link' />,
        tooltip: `${metricGroup.groupData?.link}`,
        onClick: () => window.open(metricGroup.groupData?.link),
      });
    }

    buttons.push({
      icon: <i className='fa fa-list' />,
      tooltip: `${metricGroup.groupName} ${QUESTION.CAPITALIZED_PLURAL} - Click to view`,
      onClick: () => handleDrilldown(ViewValues.QuestionList, String(metricGroup._id), String(metricGroup.groupName)),
    });

    const hasRequiredScope =
      surveyData.scopeConfig &&
      surveyData.scopeConfig?.some((scope) => scope.code === metricGroup._id && scope.required);
    if (inScope && hasRequiredScope) {
      return buttons;
    }

    if (!isDrilldown) {
      buttons.push(addBtn(String(metricGroup._id), String(metricGroup.groupName)));
    }
    return buttons;
  };

  const inheritedCards: CardGridItemProps[] = [];
  const otherCards: CardGridItemProps[] = [];
  metricGroups.forEach((metricGroup: MetricGroup) => {
    const isInherited = metricGroup.accessType === AccessType.Inherited;
    const name = `${isInherited ? 'Inherited: ' : ''}${metricGroup.groupName}`;
    const scopeTag = String(metricGroup._id);
    const { inScope, isPartial } = getScopeInfo(surveyData.scope, scopeTag, scopeType, breadcrumbs, materiality);

    const card: CardGridItemProps = {
      key: `scope-cardgriditem-metricgroup-${metricGroup._id}`,
      title: <span>{name}</span>,
      sortTitle: name,
      description: metricGroup.description ?? '',
      icon: getCardIcon(metricGroup),
      unitCount: metricGroup.universalTrackers?.length ?? 0,
      unitName: 'Metrics',
      buttons: getButtons(metricGroup),
      scopeTag,
      inScope,
      isPartial,
      initiativeId: metricGroup.initiativeId,
    };
    if (isInherited) {
      inheritedCards.push(card);
    } else {
      otherCards.push(card);
    }
  });
  if (surveyId && initiativeId && cardGroup === ViewValues.Custom) {
    otherCards.push({
      key: 'scope-cardgriditem-metricgroup-createnew',
      title: <span>Create a new metric group</span>,
      sortTitle: '000000 Create a new metric group',
      subtitle: 'Click here if you would like to create a new group of custom metrics',
      icon: <i className={'fal fa-file-circle-question'} />,
      unitCount: undefined,
      unitName: '',
      buttons: [
        {
          icon: <span className='px-3'>Create</span>,
          tooltip: `Create or Manage metric ${QUESTION.SINGULAR} groups`,
          to: `${generateUrl(ROUTES.CUSTOM_METRICS, { initiativeId, groupId: 'create' })}?surveyId=${surveyId}`,
        },
      ],
      scopeTag: '',
      inScope: false,
      isPartial: false,
    });
  }
  cardGroups.push({
    cards: inheritedCards,
  });
  cardGroups.push({
    cards: otherCards,
  });

  return cardGroups;
};

export const getQuestionPackCards = (props: CardProps) => {
  const { surveyData, appSettings, selectedView = ViewValues.QuestionPacks, setSelectedView = () => {} } = props;
  const options = [ViewValues.StandardsAndFrameworks, ViewValues.Archived].map((k: ViewValues) => ViewMap[k]);

  const coreCards: CardGridItemProps[] = [];
  const freeCards: CardGridItemProps[] = [];
  const premiumCards: CardGridItemProps[] = [];
  const recommendedAddons = appSettings.settingsRecommendedAddons;

  let requiredAddons: string[] = [];
  if (surveyData.scopeConfig) {
    surveyData.scopeConfig.forEach((s) => {
      if (s.required) {
        requiredAddons.push(s.code);
      }
    });
  }

  const availableAddons: string[] = appSettings.availableAddons ?? [];
  const allCards = getStandardsAndFrameworksCards(props);

  allCards.forEach((cardGroup) => {
    cardGroup.cards.forEach((c) => {
      if (requiredAddons.includes(c.scopeTag) || recommendedAddons.includes(c.scopeTag)) {
        return coreCards.push({
          ...c,
          isMandatory: true,
        });
      } else if (availableAddons.includes(c.scopeTag)) {
        if (hasRequiredTags(c.requiredTags)) {
          premiumCards.push(c);
        } else {
          freeCards.push(c);
        }
      }
    });
  });

  const displayedCards =
    selectedView !== ViewValues.Archived
      ? freeCards
      : getStandardsAndFrameworksCards({ ...props, isArchived: true })[0].cards;

  const customMetrics = getRequiredCustomMetrics(props);

  customMetrics.forEach((cardGroup) => {
    cardGroup.cards.forEach((card) => coreCards.push(card));
  });

  coreCards.push({
    key: 'scope-cardgriditem-metricgroup-createnew',
    title: (
      <>
        <span>Create a new metric group</span>
      </>
    ),
    sortTitle: 'zzz Create a new metric group',
    subtitle: 'Click here if you would like to create a new group of custom metrics',
    icon: <i className={'fal fa-file-circle-question'} />,
    unitCount: undefined,
    unitName: '',
    buttons: [
      {
        icon: <span className='px-3'>Create</span>,
        tooltip: `Create or Manage metric ${QUESTION.SINGULAR} groups`,
        to: `${generateUrl(ROUTES.CUSTOM_METRICS, {
          initiativeId: surveyData.initiativeId,
          groupId: 'create',
        })}?surveyId=${surveyData._id}`,
      },
    ],
    scopeTag: '',
    inScope: false,
    isPartial: false,
  });

  const cardGroups: CardGridGroup[] = [
    {
      cards: coreCards,
    },
    {
      name: CardGroupName.Free,
      cards: displayedCards,
      component: () => (
        <SurveyCardsFilter options={options} selectedView={selectedView} setSelectedView={setSelectedView} />
      ),
    },
    {
      name: CardGroupName.Premium,
      cards: premiumCards,
    },
  ];

  return cardGroups;
};

export const getAllCards = (props: CardProps) => {
  const {
    surveyData,
    appSettings,
    selectedView = ViewValues.StandardsAndFrameworks,
    setSelectedView = () => {},
  } = props;

  const options = [
    ViewValues.StandardsAndFrameworks,
    ViewValues.Sdg,
    ViewValues.Materiality,
    ViewValues.Archived,
  ].map((k: ViewValues) => ViewMap[k]);

  let coreCards: CardGridItemProps[] = [];
  const premiumCards: CardGridItemProps[] = [];
  const recommendedAddons = appSettings.settingsRecommendedAddons;

  let requiredAddons: string[] = [];
  if (surveyData.scopeConfig) {
    surveyData.scopeConfig.forEach((s) => {
      if (s.required) {
        requiredAddons.push(s.code);
      }
    });
  }

  const standardsFrameworksCards = getStandardsAndFrameworksCards(props);

  standardsFrameworksCards
    .reduce((a, c) => a.concat(c.cards), [] as CardGridItemProps[])
    .forEach((c) => {
      switch (true) {
        case requiredAddons.includes(c.scopeTag) || recommendedAddons.includes(c.scopeTag):
          coreCards.push({
            ...c,
            isMandatory: true,
          });
          break;
        case hasRequiredTags(c.requiredTags):
          premiumCards.push(c);
          break;
        default:
          break;
      }
    });

  const excludedScopeTags = [...coreCards, ...premiumCards].map((card) => card.scopeTag);
  const allCards = getCardGroups({ ...props, cardGroup: selectedView }).reduce(
    (a, c) => a.concat(c.cards),
    [] as CardGridItemProps[]
  );
  const freeCards = allCards.filter((card) => {
    return !excludedScopeTags.includes(card.scopeTag);
  });

  const customMetrics = getRequiredCustomMetrics(props);
  const customMetricCards = getSortedCustomMetricCards(customMetrics, surveyData.initiativeId);

  coreCards = [...coreCards, ...customMetricCards];

  coreCards.push({
    key: 'scope-cardgriditem-metricgroup-createnew',
    title: (
      <>
        <span>Create a new metric group</span>
      </>
    ),
    sortTitle: 'zzz Create a new metric group',
    subtitle: 'Click here if you would like to create a new group of custom metrics',
    icon: <i className={'fal fa-file-circle-question'} />,
    unitCount: undefined,
    unitName: '',
    buttons: [
      {
        icon: <span className='px-3'>Create</span>,
        tooltip: `Create or Manage metric ${QUESTION.SINGULAR} groups`,
        to: `${generateUrl(ROUTES.CUSTOM_METRICS, {
          initiativeId: surveyData.initiativeId,
          groupId: 'create',
        })}?surveyId=${surveyData._id}`,
      },
    ],
    scopeTag: '',
    inScope: false,
    isPartial: false,
  });

  const cardGroups: CardGridGroup[] = [
    {
      cards: getSortedCards(coreCards),
    },
    {
      name: CardGroupName.Free,
      cards: getSortedCards(freeCards, CardGroupName.Free),
      component: () => (
        <SurveyCardsFilter options={options} selectedView={selectedView} setSelectedView={setSelectedView} />
      ),
    },
    {
      name: CardGroupName.Premium,
      cards: getSortedCards(premiumCards, CardGroupName.Premium),
    },
  ];

  return cardGroups;
};

export const getStandardsAndFrameworksCards = (props: CardProps & { isArchived?: boolean }) => {
  const standardsCards = getStandardsCards(props);
  const frameworksCards = getFrameworksCards(props);

  const combinedCards: CardGridItemProps[] = [];
  frameworksCards.map((framework) => framework.cards.map((c) => combinedCards.push(c)));
  standardsCards.map((standard) => standard.cards.map((c) => combinedCards.push(c)));

  const filteredCombinedCards = filterCombinedCards(combinedCards);

  return [
    {
      cards: props.isArchived ? filteredCombinedCards.archived : filteredCombinedCards.latest,
    },
  ];
};

const _getCards = (groups: { [key: string]: Group }, props: CardProps) => {
  const { questionList, addBtn, handleDrilldown, breadcrumbs, surveyData, scopeType, materiality } = props;
  const cardGroups: CardGridGroup[] = [];
  const cards: CardGridItemProps[] = [];
  const isDrilldown = breadcrumbs && breadcrumbs.length > 0;

  const getButtons = (group: Group) => {
    const code = group.code;
    const name = group.name;
    const buttons: CardGridButtonProps[] = [];

    const { inScope } = getScopeInfo(surveyData.scope, code, scopeType, breadcrumbs, materiality);

    const hasRequiredScope =
      surveyData.scopeConfig && surveyData.scopeConfig.some((scope) => scope.code === code && scope.required);

    if (group.link) {
      buttons.push({
        icon: <i className='fa fa-link text-ThemeTextPlaceholder' />,
        tooltip: group.link,
        onClick: () => window.open(group.link),
      });
    }

    if (isDrilldown) {
      buttons.push({
        icon: <i className='fa fa-eye text-ThemeTextPlaceholder' />,
        tooltip: `${name} ${QUESTION.CAPITALIZED_PLURAL} - Click to view`,
        onClick: () => handleDrilldown(ViewValues.QuestionList, code, name, ViewValues.Standards),
      });
    } else if (group && group.subgroups && group.subgroups.length > 0) {
      buttons.push({
        icon: <i className='fa fa-list text-ThemeTextPlaceholder' />,
        tooltip: `${name} Groups - Click to view`,
        onClick: () => handleDrilldown(ViewValues.Group, code, name),
      });
    } else {
      buttons.push({
        icon: <i className='fa fa-list text-ThemeTextPlaceholder' />,
        tooltip: `${name} ${QUESTION.CAPITALIZED_PLURAL} - Click to view`,
        onClick: () => handleDrilldown(ViewValues.QuestionList, code, name),
      });
    }

    if (inScope && hasRequiredScope) {
      return buttons;
    }

    if (!isDrilldown) {
      const addButton = addBtn(code, name, group);
      if (addButton) {
        buttons.push(addButton);
      }
    }
    return buttons;
  };

  Object.keys(groups).forEach((scopeTag: string, i: number) => {
    const group = groups[scopeTag];
    const { inScope, isPartial } = getScopeInfo(
      surveyData.scope,
      scopeTag,
      ViewValues.StandardsAndFrameworks,
      breadcrumbs,
      materiality
    );
    const isPartialScope = inScope || isPartial;

    if (group.hidden && !isPartialScope) {
      return;
    }

    const filteredQuestionList: BaseScopeQuestion[] = questionList.filter((question) =>
      filterStandardAndFramework(question, scopeTag)
    );
    if (filteredQuestionList.length === 0) {
      return;
    }
    cards.push({
      key: `scope-cardgriditem-sandf-${scopeTag}-${i}`,
      title: (
        <>
          <span>{group.name}</span>
        </>
      ),
      sortTitle: `${group.name}-${group.code}`,
      subtitle: group.subtitle,
      description: group.description ?? '',
      icon: <img src={group.src} alt={group.name} width='40px' />,
      unitCount: filteredQuestionList.length,
      buttons: getButtons(group),
      scopeTag,
      requiredTags: group.requiredTags,
      inScope,
      isPartial,
      version: group.version,
      versionGroupCode: group.versionGroupCode,
    });
  });
  cardGroups.push({
    cards,
  });
  return cardGroups;
};
